import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import toast from 'react-hot-toast';
import { useUpdateDocument } from '@utils/api/documents/use-update-document.tsx';
import React, { useEffect, useState } from 'react';
import { WorkspaceRow } from '@components/main-workspace/workspace.tsx';

type Props = {
  isEditDocumentModalOpen: boolean;
  setIsEditDocumentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedItem: WorkspaceRow | undefined;
};

const MAX_FILENAME_LENGTH = 200;

const EditFilenameForm = ({
  isEditDocumentModalOpen,
  setIsEditDocumentModalOpen,
  selectedItem
}: Props) => {
  const { t } = useTranslation();
  const [filename, setFilename] = useState<string>('');
  const { mutate: updateSession, isLoading } = useUpdateDocument();

  useEffect(() => {
    if (isEditDocumentModalOpen) {
      setFilename(selectedItem?.name ?? '');
    }
  }, [isEditDocumentModalOpen, selectedItem]);

  const trimmed = filename.trim();
  const isTooLong = trimmed.length > MAX_FILENAME_LENGTH;
  const isEmpty = trimmed.length === 0;
  const validationError = isTooLong
    ? t('filename-max-length', { max: MAX_FILENAME_LENGTH }) ||
      `Filename must be at most ${MAX_FILENAME_LENGTH} characters.`
    : undefined;

  const handleFilenameChange = async () => {
    if (!selectedItem?.id || !selectedItem.document?.id) return;
    if (isEmpty || isTooLong) return;

    updateSession(
      {
        filename: trimmed,
        sessionId: selectedItem.session_id || selectedItem.name || '',
        documentId: selectedItem.id
      },
      {
        onSuccess: () => {
          toast.success(t('document-name-updated-successfully'));
          setIsEditDocumentModalOpen(false);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const fallback = t('failed-to-update-filename');
          const data = error?.response?.data;
          const detail = data?.detail;
          const detailMessage = Array.isArray(detail)
            ? detail
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((d: any) => d?.msg || d?.message)
                .filter(Boolean)
                .join('; ')
            : typeof detail === 'string'
              ? detail
              : undefined;
          const message =
            data?.error_description ||
            detailMessage ||
            data?.error ||
            data?.message ||
            fallback;
          toast.error(message);
        }
      }
    );
  };

  const FORM_ID = 'edit-filename-form';

  return (
    <Modal
      isOpen={isEditDocumentModalOpen}
      onClose={() => setIsEditDocumentModalOpen(false)}
      size="medium"
      title={t('edit-document-name')}
      caption={t('enter-the-new-name-for-your-document')}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={() => setIsEditDocumentModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isLoading}
        />,
        <Button
          key="save"
          type="submit"
          form={FORM_ID}
          label={isLoading ? t('updating') : t('rename')}
          size="small"
          variant="primary"
          disabled={isLoading || isEmpty || isTooLong}
        />
      ]}
    >
      <div className="py-2">
        <form
          id={FORM_ID}
          className="mt-1 sm:items-center min-w-fit"
          onSubmit={e => {
            e.preventDefault();
            if (!isLoading) handleFilenameChange();
          }}
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          <Input
            type="text"
            id="filename"
            name="filename"
            value={filename}
            onChange={e => setFilename(e.target.value)}
            label=""
            placeholder={t('enter-new-filename')}
            autoComplete="off"
            autoFocus
            className="w-full"
            maxLength={MAX_FILENAME_LENGTH}
            error={validationError}
            helperText={
              !validationError
                ? `${trimmed.length}/${MAX_FILENAME_LENGTH}`
                : undefined
            }
          />
        </form>
      </div>
    </Modal>
  );
};

export default EditFilenameForm;
